import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TranslateIcon from "@mui/icons-material/Translate";
import axios from "axios";
import liff from "@line/liff";
import { ensureLiffInit } from "../../liffClient";

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export default function AddWordModal({ open, onClose, onAdded }: Props) {
  const [word, setWord] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleAdd = async () => {
    const trimmed = word.trim();
    if (!trimmed) return;

    setLoading(true);
    setError("");
    try {
      await ensureLiffInit();
      const profile = await liff.getProfile();
      await axios.post("/v1/api/word/add", {
        userId: profile.userId,
        word: trimmed,
      });
      setWord("");
      onAdded();
      onClose();
    } catch {
      setError("登録に失敗しました。もう一度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setWord("");
    setError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #4338ca, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TranslateIcon sx={{ color: "white", fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1e293b" }}>
            単語を追加
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="例: apple, bonjour, ..."
          value={word}
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          disabled={loading}
          variant="outlined"
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              "&.Mui-focused fieldset": {
                borderColor: "#6366f1",
              },
            },
          }}
        />
        {error && (
          <Typography sx={{ color: "#ef4444", fontSize: "0.8rem", mt: 1 }}>
            {error}
          </Typography>
        )}
        <Typography sx={{ color: "#94a3b8", fontSize: "0.78rem", mt: 1.5 }}>
          翻訳と画像は自動で生成されます
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: "#64748b",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleAdd}
          disabled={loading || !word.trim()}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #4338ca, #6366f1)",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #3730a3, #4f46e5)",
            },
            "&.Mui-disabled": {
              background: "#e2e8f0",
            },
          }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "追加"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
